import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Check } from 'lucide-react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { FieldLabel } from './ui/Field'
import { api, extractApiError, isPlanLimitError } from '../lib/api'
import { DEFAULT_SPACE_TASK_STATUSES } from '../lib/types'
import { CONTAINS_LETTER, CONTAINS_EMOJI } from '../lib/validators'

const SPACE_COLOURS = [
  '#0EA5E9',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#A855F7',
  '#EC4899',
  '#3B82F6',
  '#14B8A6',
  '#F97316',
  '#94A3B8',
]

/** Views every new Space ships with. There used to be a second
 *  "Customize" step on this dialog that let admins toggle these per
 *  Space at create time — that step was removed because nearly every
 *  admin just clicked through with the defaults and tweaked later
 *  from the Space's settings. Per-Space view edits still live on the
 *  Space header; this constant is just the seed. */
const DEFAULT_SPACE_VIEWS = ['list', 'board']

const spaceSchema = z.object({
  name: z
    .string({ message: 'Space Name is required.' })
    .trim()
    .min(1, 'Space Name is required.')
    .min(2, 'Space Name must be at least 2 characters.')
    .max(100, 'Space Name must be at most 100 characters.')
    // Numbers-only / symbols-only names aren't meaningful — require a letter.
    .regex(CONTAINS_LETTER, 'Space Name must contain at least one letter.')
    // A Space Name must read as plain text — no emojis (matches Task Name).
    .refine((v) => !CONTAINS_EMOJI.test(v), 'Space Name cannot contain emojis.'),
  description: z
    .string()
    .trim()
    .max(500, 'Description must be at most 500 characters.')
    .optional()
    .or(z.literal('')),
  // First Project, created inside the Space in the same step. Required —
  // every Space starts with at least one Project.
  firstProjectName: z
    .string({ message: 'Project Name is required.' })
    .trim()
    .min(1, 'Project Name is required.')
    .min(2, 'Project Name must be at least 2 characters.')
    .max(100, 'Project Name must be at most 100 characters.')
    // Numbers-only / symbols-only names aren't meaningful — require a letter.
    .regex(CONTAINS_LETTER, 'Project Name must contain at least one letter.')
    // A Project Name must read as plain text — no emojis (matches Task Name).
    .refine((v) => !CONTAINS_EMOJI.test(v), 'Project Name cannot contain emojis.'),
})

export function SpaceCreateDialog({
  open,
  workspaceId,
  // Optional Department to create the Space in. A uuid pins the Space to
  // that Department; `null` makes it an explicit company-wide (org-wide)
  // Space; `undefined` (the default) lets the backend decide — the
  // creator's own Department, or org-wide for a company-access creator.
  departmentId,
  // When true this creates the caller's own PERSONAL Space (private, owned by
  // them, no Department) — the "Spaces" sidebar section. The privacy toggle is
  // hidden (personal Spaces are always private).
  personal = false,
  onClose,
  onCreated,
}) {
  const qc = useQueryClient()
  const [color, setColor] = useState(SPACE_COLOURS[0])
  // Default to SHARED (not private). A private Space is visible only to its
  // owner + explicit invitees, which surprised admins: a Space they created
  // in a Department was invisible to that Department's members. Shared-by-
  // default means a new Space is visible to its Department (or company-wide
  // when it has none); the "Make Private" toggle is still there to opt in.
  const [isPrivate, setIsPrivate] = useState(false)

  const form = useForm({
    resolver: zodResolver(spaceSchema),
    defaultValues: { name: '', description: '', firstProjectName: '' },
  })

  useEffect(() => {
    if (open) {
      setColor(SPACE_COLOURS[0])
      setIsPrivate(false)
      form.reset({ name: '', description: '', firstProjectName: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error('Missing workspace.')
      const values = form.getValues()
      // Same-step first Project: when the admin names one, the backend creates
      // the Project (Folder) AND puts the Space's single starter "Tasks List"
      // inside it — so there's exactly one List, not two. (The old flow created
      // the Project separately and moved the root List in, which left a
      // duplicate alongside the Project's own auto-created List.)
      const projectName = values.firstProjectName?.trim()
      const res = await api.post(`/workspaces/${workspaceId}/spaces`, {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        color,
        // Personal Spaces are always private; the backend enforces it too.
        isPrivate: personal ? true : isPrivate,
        ...(personal ? { isPersonal: true } : {}),
        // Only sent when the caller pinned a Department (or explicitly
        // org-wide via null). Omitted entirely otherwise so the backend
        // keeps its own default behaviour. A personal Space has no Department.
        ...(personal
          ? {}
          : departmentId !== undefined
            ? { departmentId }
            : {}),
        ...(projectName ? { firstProjectName: projectName } : {}),
        // Defaults — admins customise these post-create from the
        // Space's own header (no more two-step wizard at create time).
        defaultViews: DEFAULT_SPACE_VIEWS,
        taskStatuses: DEFAULT_SPACE_TASK_STATUSES,
      })
      return res.data
    },
    onSuccess: (created) => {
      toast.success('Space added successfully.')
      void qc.invalidateQueries({ queryKey: ['spaces', workspaceId] })
      void qc.invalidateQueries({ queryKey: ['folders', created.id] })
      void qc.invalidateQueries({ queryKey: ['lists', created.id] })
      onClose()
      onCreated?.(created.id)
    },
    onError: (err) => {
      // Plan-cap hit: the global API interceptor already shows the single
      // amber "upgrade your plan" warning, so suppress our own error toast.
      if (isPlanLimitError(err)) return
      const { message, fields } = extractApiError(err)
      if (fields?.name) {
        form.setError('name', { message: fields.name })
      } else {
        toast.error(message)
      }
    },
  })

  function handleSubmit() {
    createMutation.mutate()
  }

  // Live max-length feedback. The native `maxLength` cap silently blocks
  // typing once a field is full (the same trap ui/Field.jsx documents for
  // the Full Name field), so without this the user just sees their
  // keystrokes vanish. Fires the moment a field fills to its cap; the
  // shared toast id collapses repeats (e.g. multiple paste attempts) into
  // one message. Wording matches the schemas' own max messages exactly.
  const notifyAtMax = (label, max) => (e) => {
    if ((e.target.value ?? '').length >= max) {
      toast.error(`${label} must be at most ${max} characters.`, {
        id: 'space-create-max-length',
      })
    }
  }

  const initial = (form.watch('name').trim().charAt(0) || 'S').toUpperCase()

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create a Space"
      description="A Space represents teams, departments, or groups, each with its own Projects, Lists, workflows, and settings."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="space-basic-form"
            loading={createMutation.isPending}
          >
            Create Space
          </Button>
        </>
      }
    >
      <form
        id="space-basic-form"
        onSubmit={form.handleSubmit(handleSubmit)}
        noValidate
        className="space-y-4"
      >
        <div>
          <FieldLabel label="Icon & Space Name" required />
          <div className="flex items-stretch gap-2">
            <button
              type="button"
              onClick={() => {
                const idx = SPACE_COLOURS.indexOf(color)
                setColor(SPACE_COLOURS[(idx + 1) % SPACE_COLOURS.length])
              }}
              aria-label="Change icon colour"
              title="Change icon colour"
              className="h-10 w-10 shrink-0 rounded-md text-white font-semibold flex items-center justify-center cursor-pointer ring-1 ring-inset ring-black/10 hover:brightness-110"
              style={{ backgroundColor: color }}
            >
              {initial}
            </button>
            <input
              {...form.register('name', {
                onChange: notifyAtMax('Space Name', 100),
              })}
              type="text"
              placeholder="Enter a Space Name"
              minLength={2}
              // Soft limit: no DOM maxLength — the field accepts input past the
              // cap and flags it via notifyAtMax + the RHF/Zod error below; the
              // schema (spaces.dto.js) blocks the actual save.
              className={
                'flex-1 h-10 px-3 rounded-md border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors ' +
                (form.formState.errors.name?.message
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
              }
            />
          </div>
          {form.formState.errors.name?.message ? (
            <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {form.formState.errors.name.message}
            </p>
          ) : null}
          <div className="mt-2 grid grid-cols-10 gap-1.5">
            {SPACE_COLOURS.map((c) => {
              const isActive = c === color
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  aria-label={`Pick colour ${c}`}
                  title={c}
                  className={
                    'relative h-6 w-6 rounded-md transition-transform cursor-pointer motion-safe:hover:scale-110 ' +
                    (isActive ? 'ring-2 ring-offset-2 ring-[color:var(--color-ink-strong)]' : '')
                  }
                  style={{ backgroundColor: c }}
                >
                  {isActive ? (
                    <Check
                      size={12}
                      className="absolute inset-0 m-auto text-white drop-shadow"
                      aria-hidden="true"
                    />
                  ) : null}
                </button>
              )
            })}
          </div>
        </div>

        <div>
          <FieldLabel label="Description (Optional)" />
          <textarea
            {...form.register('description', {
              onChange: notifyAtMax('Description', 500),
            })}
            placeholder="Enter a Description"
            rows={2}
            className={
              'w-full px-3 py-2 rounded-md border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors ' +
              (form.formState.errors.description?.message
                ? 'border-[color:var(--color-priority-urgent)]'
                : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
            }
          />
          {form.formState.errors.description?.message ? (
            <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {form.formState.errors.description.message}
            </p>
          ) : null}
        </div>

        {/* First Project — created inside the Space in the same step (with a
         *  starter List). Required: every Space starts with one Project. */}
        <div>
          <FieldLabel label="First Project" required htmlFor="space-first-project" />
          <input
            id="space-first-project"
            {...form.register('firstProjectName', {
              onChange: notifyAtMax('Project Name', 100),
            })}
            type="text"
            placeholder="Enter a Project Name"
            minLength={2}
            className={
              'w-full h-10 px-3 rounded-md border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors ' +
              (form.formState.errors.firstProjectName?.message
                ? 'border-[color:var(--color-priority-urgent)]'
                : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
            }
          />
          {form.formState.errors.firstProjectName?.message ? (
            <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {form.formState.errors.firstProjectName.message}
            </p>
          ) : (
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Creates a Project inside the Space (with a starter List).
            </p>
          )}
        </div>

        {/* Personal Spaces are always private — no toggle. */}
        {personal ? null : (
          <ToggleRow
            label="Make Private"
            description="Only you and people you invite have access. Switch off to share with everyone in your department."
            value={isPrivate}
            onChange={setIsPrivate}
          />
        )}
      </form>
    </Modal>
  )
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ' +
        (checked
          ? 'bg-[color:var(--color-accent-strong)]'
          : 'bg-slate-300')
      }
    >
      <span
        aria-hidden="true"
        className={
          'inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ' +
          (checked ? 'translate-x-[1.375rem]' : 'translate-x-0.5')
        }
      />
    </button>
  )
}

function ToggleRow({ label, description, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
      <div className="min-w-0">
        <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">{label}</p>
        {description ? (
          <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">{description}</p>
        ) : null}
      </div>
      <Toggle checked={value} onChange={onChange} />
    </div>
  )
}
